import { type Theme } from '@mui/material/styles';

import {
  type SxProps,
  type SxPropsArray,
  type SxPropsCallback,
  type SystemStyleObject,
} from '../../types/mui/sx';

import { sxPropsAreArray } from './typeguards';

type WithFlexSx<T> = (T | undefined)[] | T | undefined;

export function mergeSx(
  sx: WithFlexSx<SystemStyleObject>,
  ...rest: (SystemStyleObject | undefined)[]
): SystemStyleObject[];

export function mergeSx(
  sx: WithFlexSx<SxPropsCallback | SystemStyleObject>,
  ...rest: (SxPropsCallback | SystemStyleObject | undefined)[]
): (SxPropsCallback | SystemStyleObject)[];

export function mergeSx(sx: WithFlexSx<SxProps>, ...rest: (SxProps | undefined)[]): SxPropsArray;

/**
 * Merges the provided Material UI SX Props, {@link SxProps}, into a single SX Prop,
 * {@link SxProps}, represented as an array, {@link SxPropsArray}.  Content that is defined in
 * {@link SxProps} that are defined later in the argument list will override content that is defined
 * in {@link SxProps} that are defined earlier in the argument list.
 */
export function mergeSx(sx: WithFlexSx<SxProps>, ...rest: (SxProps | undefined)[]): SxPropsArray {
  let base: SxProps[] = [];
  if (sx !== undefined) {
    if (Array.isArray(sx)) {
      base = sx.filter((item): item is SxProps => item !== undefined);
    } else {
      base = [sx];
    }
  }
  return [...base, ...rest].reduce((acc: SxPropsArray, item: SxProps | undefined) => {
    if (item !== undefined) {
      if (sxPropsAreArray(item)) {
        return [...acc, ...item];
      }
      return [...acc, item];
    }
    return acc;
  }, [] as SxPropsArray);
}

const isRecordType = (sx: unknown): sx is Record<string, unknown> =>
  sx !== null && typeof sx === 'object' && !Array.isArray(sx) && typeof sx !== 'function';

/**
 * Deeply merges two Material UI System Style Objects, {@link SystemStyleObject}, together,
 * returning a new {@link SystemStyleObject} that contains the merged content of both source
 * objects. In cases where both source objects define the same property, the value from the source
 * object, {@link source}, will override the value from the target object, {@link target}.
 *
 * While this method is generic and can be used to merge any two record types, it is specifically
 * designed to handle the merging of {@link SystemStyleObject} instances, and should not be relied
 * on more generally for other use cases.
 *
 * @param {T} target
 *   The target System Style Object, {@link SystemStyleObject}, that will be merged into.
 * @param {R} source
 *   The source System Style Object, {@link SystemStyleObject}, that will be merged from.
 *
 * @returns {T & R}
 *   A new System Style Object, {@link SystemStyleObject}, that results from deeply merging the
 *   target and source objects.
 */
const deepMergeSystemStyleObjects = <
  T extends Record<string, unknown>,
  R extends Record<string, unknown>,
>(
  target: T,
  source: R,
): R & T => {
  const output: T = { ...target };
  if (isRecordType(target) && isRecordType(source)) {
    for (const key in source) {
      const sourceValue = source[key];
      if (isRecordType(sourceValue)) {
        if (key in target) {
          const targetValue = target[key];
          if (isRecordType(targetValue)) {
            output[key] = deepMergeSystemStyleObjects(targetValue, sourceValue);
          } else {
            Object.assign(output, { [key]: sourceValue });
          }
        } else {
          Object.assign(output, { [key]: sourceValue });
        }
      } else if (sourceValue !== undefined) {
        Object.assign(output, { [key]: sourceValue });
      }
    }
  }
  return output as R & T;
};

/**
 * Flattens an array of Material UI System Style Objects, {@link SystemStyleObject}, into a single
 * System Style Object, {@link SystemStyleObject}, by deeply merging each item in the array from
 * left to right.  This method is particularly useful when the evaluated result of multiple SX
 * objects is needed, since the {@link mergeSx} method produces an array of SX objects - which may
 * be used directly by MUI.
 *
 * @example
 * ```ts
 * // Returns [ { color: 'red' }, { backgroundColor: 'blue' }, { padding: 2 } ]
 * const mergedViaMergeSx = mergeSx({ color: 'red' }, { backgroundColor: 'blue' }, { padding: 2 });
 *
 * // Returns { color: 'red', backgroundColor: 'blue', padding: 2 }
 * const flattenedViaFlattenSystemStyleObjectsArray = flattenSystemStyleObjectsArray(
 *  mergedViaMergeSx,
 * );
 * ```
 */
export const flattenSystemStyleObjectsArray = (
  sx: SystemStyleObject[],
): NonNullable<SystemStyleObject> =>
  sx
    .slice(1)
    .reduce(
      (acc: NonNullable<SystemStyleObject>, item) =>
        item ? deepMergeSystemStyleObjects(acc, item) : acc,
      sx[0] ?? {},
    );

/**
 * Invokes the provided Material UI SX Props, {@link SxProps}, flattening {@link SxProps} defined
 * as {@link SxPropsCallback} or {@link SxProps} defined as an array {@link SxPropsArray} into a
 * single {@link SystemStyleObject}.  Before the {@link SxProps} are flattened and evaluated, they
 * are merged together into an array via the {@link mergeSx} method.
 *
 * This method can be used in cases where nested Sx properties are being defined and need to be of
 * the form {@link SystemStyleObject} and not of the un-evaluated form {@link SxProps}.
 *
 * @param {Theme} theme The MUI {@link Theme} object.
 * @param {SxProps | undefined} sx The first set of {@link SxProps} to evaluate.
 * @param {(SxProps | undefined)[]} rest The rest of the {@link SxProps} to evaluate.
 *
 * @returns {SystemStyleObject}
 *   The flattened, evaluated {@link SystemStyleObject} that results from evaluating each argument
 *   with the provided theme, {@link Theme}.
 */
export function invokeSx(
  theme: Theme,
  sx: WithFlexSx<SxProps>,
  ...rest: (SxProps | undefined)[]
): NonNullable<SystemStyleObject> {
  const invoke = (item: SxProps | undefined): NonNullable<SystemStyleObject> => {
    if (item === undefined) {
      return {};
    }
    if (sxPropsAreArray(item)) {
      return item.reduce((acc: NonNullable<SystemStyleObject>, s) => {
        if (typeof s === 'boolean') {
          return acc;
        } else if (typeof s === 'function') {
          const invoked = s(theme);
          if (invoked) {
            return deepMergeSystemStyleObjects(acc, invoked);
          }
          return acc;
        } else if (s) {
          return deepMergeSystemStyleObjects(acc, s);
        }
        return acc;
      }, {} as NonNullable<SystemStyleObject>);
    }
    if (typeof item === 'function') {
      const invoked = item(theme);
      return invoked ?? {};
    }
    return item ?? {};
  };
  return mergeSx(sx, ...rest).reduce(
    (acc: NonNullable<SystemStyleObject>, item) =>
      typeof item === 'boolean' ? acc : deepMergeSystemStyleObjects(acc, invoke(item)),
    {} as NonNullable<SystemStyleObject>,
  );
}
